import { Component, OnInit, ViewChild } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { AgilityRiskService } from '../../providers/agility-risk.service';
import { TranslateService } from '@ngx-translate/core';
import { Assessment } from '../../models/assessment';
import { AssessmentStatus } from '../../models/assessment-status';
import { NameEntityTypeFilter } from '../../models/name-entity-type-filter';
import { PageRequest } from '../../models/page-request';
import { ActivatedRoute, Router } from '@angular/router';
import { Table } from 'primeng/table';
import { FilterService } from 'primeng/api';
import { ConfirmationModalComponent } from '../shared/modals/confirmation-modal/confirmation-modal.component';
import { AgilityRiskMessageService } from '../../providers/agility-risk-message.service';
import { AgilityRiskPermissionService } from '../../providers/agility-risk-permission.service';
import { permissions } from '../../agility-risk.constants';
import { AgilityRiskSpinnerService } from '../../providers/agility-risk-spinner.service';

@Component({
  selector: 'lib-risk-dashboard',
  templateUrl: './risk-dashboard.component.html',
  styleUrls: ['./risk-dashboard.component.scss']
})
export class RiskDashboardComponent implements OnInit {
  @ViewChild('assessmentDt') private assessmentDt: Table;
  public assessments: Array<Assessment> = [];
  public assessmentTableSize: number;
  public assessmentTableTotalElements: number;
  public assessmentTableTotalPages: number;
  public assessmentStatus = AssessmentStatus;
  public statusOptions = [
    {
      label: this.translateService.instant('common.approved'),
      value: this.assessmentStatus.current
    },
    {
      label: this.translateService.instant('common.incomplete'),
      value: this.assessmentStatus.incomplete
    },
    {
      label: this.translateService.instant('common.completed'),
      value: this.assessmentStatus.complete
    }
  ];
  public size = 15;
  public rangeDueDates: any;
  public rangeCreatedDates: any;
  public permissionTypes = {
    edit: 'EDIT',
    control: 'CONTROL'
  };
  public permissions = permissions;

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private dialogService: DialogService,
              private agilityRiskService: AgilityRiskService,
              private translateService: TranslateService,
              private filterService: FilterService,
              private messageService: AgilityRiskMessageService,
              private permissionsService: AgilityRiskPermissionService,
              private spinnerService: AgilityRiskSpinnerService) {
  }

  ngOnInit(): void {
    this.getRiskAssessments();

    this.filterService.register('dueDateRangeFilter', (value, filter): boolean => {
      if (!this.rangeDueDates || !this.rangeDueDates?.length) {
        return true;
      }

      // get the from/start value
      const dayInMilliseconds: number = 86400000;
      const s = this.rangeDueDates[0].getTime();
      let e;

      // the to/end value might not be set
      // use the from/start date and add 1 day
      // or the to/end date and add 1 day
      if (this.rangeDueDates[1]) {
        e = this.rangeDueDates[1].getTime() + dayInMilliseconds;
      } else {
        e = s + dayInMilliseconds;
      }

      // compare it to the actual values
      return value?.getTime() >= s && value?.getTime() <= e;
    });

    this.filterService.register('createdDateRangeFilter', (value, filter): boolean => {
      if (!this.rangeCreatedDates || !this.rangeCreatedDates?.length) {
        return true;
      }

      // get the from/start value
      const dayInMilliseconds: number = 86400000;
      const s = this.rangeCreatedDates[0].getTime();
      let e;

      // the to/end value might not be set
      // use the from/start date and add 1 day
      // or the to/end date and add 1 day
      if (this.rangeCreatedDates[1]) {
        e = this.rangeCreatedDates[1].getTime() + dayInMilliseconds;
      } else {
        e = s + dayInMilliseconds;
      }

      // compare it to the actual values
      return value?.getTime() >= s && value?.getTime() <= e;
    });
  }

  getRiskAssessments(page?: number): void {
    const pageRequest = new PageRequest<NameEntityTypeFilter>();
    pageRequest.page = page || 0;
    pageRequest.size = this.size;

    this.spinnerService.start();

    this.agilityRiskService.getRiskAssessments(pageRequest).subscribe({
      next: (res: any) => {
        this.assessments = res.content;
        this.assessmentTableSize = res.size;
        this.assessmentTableTotalElements = res.totalElements;
        this.assessmentTableTotalPages = res.totalPages;

        this.assessments.forEach((assessment: Assessment) => {
          assessment.createdDate = assessment.createdDate ? new Date(assessment.createdDate) : null;
          assessment.dueDate = assessment.dueDate ? new Date(assessment.dueDate) : null;

          if (assessment.status === this.assessmentStatus.incomplete && assessment.percentInt === 100) {
            assessment.status = this.assessmentStatus.complete;
          }
        });
      }
    }).add(() => this.spinnerService.stop());
  }

  onPage({ first, rows }: { first: number; rows: number }): void {
    const page = first ? first / rows : 0;
    this.getRiskAssessments(page);
  }

  openCustomFieldsModal() {
    this.router.navigate(['custom-fields-management/RISK'], {relativeTo: this.activatedRoute.parent});
  }

  deleteAssessment(assessment: Assessment) {
    // can't delete if can't control or not an admin/partner
    if (!this.canDelete(assessment.parent)) {
      return;
    }

    const ref = this.dialogService.open(ConfirmationModalComponent, {
      data: {
        body: this.translateService.instant('assessment.deleteAssessmentModal.body')
      },
      header: this.translateService.instant('assessment.deleteAssessmentModal.header'),
      width: '35%'
    });

    ref.onClose.subscribe((result: any) => {
      if (result) {
        this.agilityRiskService.deleteRiskAssessment(assessment.id)
          .subscribe(
            () => {
              this.messageService.success(this.translateService.instant('common.success'));
              this.getRiskAssessments();
            },
            () => {
              this.messageService.error(this.translateService.instant('common.error'));
            }
          );
      }
    });
  }

  canDelete(entity: any): boolean {
    // Requirements are: 1) admin or higher role and 2) edit or control permission
    return this.permissionsService.canEdit(entity);
  }

  canView(entity: any): boolean {
    // Requirements are: 1) Admin or higher role and 2) view or edit or control permission
    return this.permissionsService.canView(entity);
  }
}
